import React, { PropsWithChildren, ReactNode } from 'react'
import clsx from 'clsx'
import { Footer as FooterStyles } from '@airbus/elements/elements'
import { ColorModeProvider, createUseStyles, useTheme } from '../../lib/styling'
import useMediaQuery from '../../lib/hooks/useMediaQuery'
import Link from '../Link'
import { BreakpointName, ThemeMode } from '../helperTypes'
import { CommonProps, LinkTarget } from '../helperTypes'

export type FooterLinkOption = {
  href: string
  label: string
  target?: LinkTarget
}

type Props = PropsWithChildren<{
  /** copyright to show ex. airbus 20xx / skywise 20xx
   * @default "airbus 2023"
   */
  copyright?: string
  /** show mobile variant of footer until specified breakpoint
   * @default "sm"
   */
  mobileUntil?: Exclude<BreakpointName, 'xxs' | 'xs'>
  /** Array of object with href to navigate and labels to display.
   * This is displayed by default. */
  primaryLinks: FooterLinkOption[]
  /** render function to add extended content to the footer */
  renderExtended?: () => ReactNode
  /**
   * Theme/color that will impact children variants
   * @default "dark"
   */
  theme?: ThemeMode
}>

export type FooterProps = Props & CommonProps

const CURRENT_YEAR = new Date().getFullYear()

const useStyles = createUseStyles((theme) => {
  const styles = FooterStyles(theme)
  const carbongridAsset = theme.asset.image.carbongrid
  return {
    ...styles,
    carbongrid: {
      backgroundImage: `url('data:image/png;base64,${carbongridAsset.value}')`,
      backgroundSize: `${carbongridAsset.width}px ${carbongridAsset.height}px`,
    },
  }
}, 'footer')

const Footer = (props: FooterProps) => {
  const { children, theme = 'dark', ...rest } = props
  return (
    <ColorModeProvider mode={theme === 'light' ? 'default' : 'dark'}>
      <FooterWrapper {...rest}>{children}</FooterWrapper>
    </ColorModeProvider>
  )
}

// separated components to ensure useStyles gets
// correct theme context value from ColorModeProvider
const FooterWrapper = (props: FooterProps) => {
  const {
    className,
    children,
    copyright = `Airbus ${CURRENT_YEAR}`,
    mobileUntil = 'sm',
    primaryLinks,
    renderExtended,
    style,
    ...rest
  } = props
  const classes = useStyles()
  const { responsive } = useTheme()
  const isMobile = useMediaQuery(`(max-width: ${responsive.breakpoint[mobileUntil]}px)`)
  return (
    <footer role='contentinfo' className={clsx(classes.root, isMobile && classes._mobile)} {...rest}>
      {renderExtended && <div className={classes.extended}>{renderExtended()}</div>}
      {children && <div className={clsx(classes.secondary, classes.carbongrid)}>{children}</div>}
      <div className={clsx(className, classes.primary)} style={style}>
        {primaryLinks && (
          <div className={classes.primaryLinks}>
            {primaryLinks.map((link, i) => {
              return (
                <Link key={i} href={link.href} size='small' target={link.target || '_self'}>
                  {link.label}
                </Link>
              )
            })}
          </div>
        )}
        <div className={classes.copyright}>&#169;{copyright}</div>
        <div className={classes.logo}></div>
      </div>
    </footer>
  )
}

export default Footer
